import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import renderMessage from '../renderMessage';
import { Button, Icons } from '../../base/BootstrapComponents/BootstrapComponents';
import {
  Dialog, DialogContent, DialogFooter, DialogTitle,
} from '../../base/Dialog/Dialog';
import LeafletMap from '../../base/Map/LeafletMap';
import RectangleDrawer from '../../base/Map/RectangleDrawer';
import TextInput from '../TextInput/TextInput';
import './map-input.scss';

const isFiniteNumber = value => typeof value === 'number' && Number.isFinite(value);

const normalizePoint = point => {
  if (!Array.isArray(point) || point.length < 2) {
    return null;
  }
  const lat = Number(point[0]);
  const lng = Number(point[1]);
  if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) {
    return null;
  }
  return [lat, lng];
};

const normalizeBounds = bounds => {
  if (!Array.isArray(bounds) || bounds.length < 2) {
    return null;
  }
  const firstPoint = normalizePoint(bounds[0]);
  const secondPoint = normalizePoint(bounds[1]);
  if (!firstPoint || !secondPoint) {
    return null;
  }
  return [firstPoint, secondPoint];
};

export default function MapInput(props) {
  const {
    className = '',
    disabled = false,
    fieldState = {
      error: '',
      touched: false,
    },
    inputProps = {},
    // Это не криптография! Если ваш анализатор этого не понимает не используйте этот анализатор
    // nosemgrep
    controlId = `map-input-${Math.random() * 10000}`,
    placeholder = 'Границы не выбраны',
    inputClassName = '',
    variant = '',
    clearValue = null,
  } = props;
  const {
    error,
    isTouched: touched,
  } = fieldState;
  const [map, setMap] = useState(null);
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [draftBounds, setDraftBounds] = useState(null);

  const currentBounds = useMemo(() => normalizeBounds(inputProps.value), [inputProps.value]);
  const name = inputProps?.name || '';
  const isError = Boolean(fieldState?.error);
  const hasOnChange = Boolean(inputProps && typeof inputProps.onChange === 'function');
  const canConfirm = Boolean(normalizeBounds(draftBounds));

  useEffect(() => {
    if (isOpen) {
      setDraftBounds(currentBounds);
    }
  }, [isOpen, currentBounds]);

  const formatBounds = bounds => {
    const normalized = normalizeBounds(bounds);
    if (!normalized) {
      return placeholder;
    }
    const [[lat1, lng1], [lat2, lng2]] = normalized;
    return `[${lat1}, ${lng1}] - [${lat2}, ${lng2}]`;
  };

  const emitChange = value => {
    if (!hasOnChange) {
      return;
    }
    inputProps.onChange({
      target: {
        value,
        name,
      },
    });
  };

  const openDialog = () => {
    if (disabled) {
      return;
    }
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  const handleRectangleChange = polygon => {
    const nextBounds = normalizeBounds(polygon);
    setDraftBounds(nextBounds);
  };

  const handleConfirm = () => {
    const nextBounds = normalizeBounds(draftBounds);
    if (!nextBounds) {
      return;
    }
    emitChange(nextBounds);
    closeDialog();
  };

  const handleClear = () => {
    emitChange(clearValue);
    setDraftBounds(null);
    closeDialog();
  };

  const onReset = event => {
    inputProps.onChange({ target: { value: null, name: inputProps.name } });
    event.preventDefault();
    event.stopPropagation();
  };

  const inputValue = currentBounds ? formatBounds(currentBounds) : '';

  return (
    <div className={classNames('form-control-wrapper', className, { material: variant === 'material' }, { default: variant !== 'material' })}>
      <TextInput
        {...props}
        readOnly
        inputBefore={(
          <div id={controlId} className="select-arrow-wrapper">
            <Icons customIcon onClick={openDialog} width={18} height={18} icon="pencil" />
          </div>
        )}
        inputAfter={(
          <div id={controlId} className="select-arrow-wrapper">
            {inputValue ? <Icons customIcon onClick={onReset} width={18} height={18} icon="close" /> : null}
          </div>
        )}
        className={classNames('custom-select', { 'is-invalid': isError && touched }, inputClassName)}
        fieldState={fieldState}
        // containerRef={anchorElement}
        inputProps={{
          name,
          onChange: () => {},
          value: inputValue || placeholder,
          onClick: openDialog,
        }}
      />
      {isError ? <div className="invalid-feedback">{renderMessage(fieldState.error)}</div> : null}
      <Dialog open={isOpen} onClose={closeDialog} hideByOutside className="map-input-dialog" size="xl">
        <DialogTitle header={formatMessage({ id: 'map.input.title' })} title={<Icons onClick={closeDialog} customIcon icon="close" className="close" />} />
        <DialogContent>
          <div style={{ height: 420 }}>
            <LeafletMap setMap={setMap}>
              <RectangleDrawer
                drawAlways
                bound={draftBounds}
                onChange={handleRectangleChange}
              />
            </LeafletMap>
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="secondary" onClick={handleClear} disabled={!hasOnChange}>
            Очистить
          </Button>
          <Button variant="secondary" onClick={closeDialog}>
            Отмена
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            Применить
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
